import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { SITE_NAME } from '@/lib/site-config';

const platformLinks = [
  { label: 'Products', href: '/products' },
  { label: 'For Brands', href: '/for-brands' },
  { label: 'For Reviewers', href: '/for-reviewers' },
  { label: 'Blog', href: '/blog' },
  { label: 'Case Studies', href: '/case-studies' },
];

const companyLinks = [
  { label: 'About', href: '/about' },
  { label: 'Help', href: '/help' },
  { label: 'Contact', href: '/contact' },
  { label: 'Careers', href: '/careers' },
];

const legalLinks = [
  { label: 'Terms & Conditions', href: '/terms' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Refund Policy', href: '/refund' },
];

export default function Footer() {
  return (
    <footer className="bg-foreground text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 mb-14">
          {/* Col 1: Brand — wider */}
          <div className="lg:col-span-4 space-y-5">
            <div className="flex items-center gap-2.5">
              <AppLogo size={34} />
              <span className="font-heading font-bold text-white tracking-tight">
                IndianReview<span className="text-primary">Club</span>
              </span>
            </div>
            <p className="text-sm text-white/50 leading-relaxed max-w-xs">
              India's most trusted product review platform. Brands submit products for testing. Creators review and earn via UPI.
            </p>
            {/* Trust badges */}
            <div className="flex flex-wrap gap-2 pt-1">
              {['AI Verified', 'UPI Payouts', 'GST Invoices']?.map((badge) => (
                <span key={badge} className="text-[10px] font-semibold uppercase tracking-wide text-white/40 border border-white/10 px-2.5 py-1 rounded-full">
                  {badge}
                </span>
              ))}
            </div>
          </div>

          {/* Col 2: Platform */}
          <div className="lg:col-span-2 space-y-4">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/30">Platform</span>
            <ul className="space-y-2.5">
              {platformLinks?.map((l) => (
                <li key={l?.href}>
                  <Link href={l?.href} className="text-sm text-white/55 hover:text-white transition-colors">
                    {l?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Company */}
          <div className="lg:col-span-2 space-y-4">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/30">Company</span>
            <ul className="space-y-2.5">
              {companyLinks?.map((l) => (
                <li key={l?.href}>
                  <Link href={l?.href} className="text-sm text-white/55 hover:text-white transition-colors">
                    {l?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Legal + App */}
          <div className="lg:col-span-4 space-y-4">
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/30">Legal</span>
            <ul className="space-y-2.5 mb-6">
              {legalLinks?.map((l) => (
                <li key={l?.href}>
                  <Link href={l?.href} className="text-sm text-white/55 hover:text-white transition-colors">
                    {l?.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-2 pt-2">
              <Link href="/app-download" className="flex items-center gap-3 bg-white/8 hover:bg-white/14 border border-white/10 rounded-xl px-4 py-2.5 transition-colors group">
                <Icon name="DevicePhoneMobileIcon" size={18} className="text-white/60 group-hover:text-white transition-colors" />
                <div>
                  <div className="text-[9px] text-white/35 uppercase tracking-wide">Get it on</div>
                  <div className="text-xs font-semibold text-white/80 group-hover:text-white transition-colors">Google Play</div>
                </div>
              </Link>
              <Link href="/app-download" className="flex items-center gap-3 bg-white/8 hover:bg-white/14 border border-white/10 rounded-xl px-4 py-2.5 transition-colors group">
                <Icon name="DevicePhoneMobileIcon" size={18} className="text-white/60 group-hover:text-white transition-colors" />
                <div>
                  <div className="text-[9px] text-white/35 uppercase tracking-wide">Download on</div>
                  <div className="text-xs font-semibold text-white/80 group-hover:text-white transition-colors">App Store</div>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/30">
            © 2026 {SITE_NAME}. All rights reserved.
          </p>
          <p className="text-xs text-white/30">
            Made with ❤️ in India 🇮🇳
          </p>
        </div>
      </div>
    </footer>
  );
}